import type { AddressBook } from '@/types/Models/AddressBook'
import type { ServerOptions, Header } from 'vue3-easy-data-table'
import { ref, computed, watch, type Ref, type ComputedRef } from 'vue'
import { useAddressBookStore } from '@/store/addressBook'

export const useAddressBookTable = (
  getCompanyId: () => string | null,
  options: { watchCompanyId?: boolean; type?: string } = {},
) => {
  const addressBookStore = useAddressBookStore()

  const dataTable = ref()
  const search: Ref<string> = ref('')
  const serverOptions: Ref<ServerOptions> = ref({ page: 1, rowsPerPage: 50 })

  const items: ComputedRef<AddressBook[]> = computed(() => addressBookStore.items)
  const loading: ComputedRef<boolean> = computed(() => addressBookStore.loading)
  const serverItemsLength: ComputedRef<number> = computed(() => addressBookStore.count)

  const headers: Header[] = [
    { text: 'Name', value: 'name' },
    { text: 'Company', value: 'company' },
    { text: 'Country', value: 'countryCode' },
    { text: 'City', value: 'city' },
    { text: 'Address', value: 'addressLine' },
    { text: 'Phone', value: 'phone' },
    { text: 'Email', value: 'email' },
  ]

  const buildParams = () => {
    const companyId = getCompanyId()
    return {
      page: String(serverOptions.value.page),
      limit: String(serverOptions.value.rowsPerPage),
      ...(search.value ? { search: search.value } : {}),
      ...(companyId ? { company_id: companyId } : {}),
      ...(options.type ? { type: options.type } : {}),
    }
  }

  const fetchData = () => {
    addressBookStore.fetchAll(buildParams())
  }

  const updateServerOptions = (newOptions: ServerOptions) => {
    serverOptions.value = newOptions
    fetchData()
  }

  let searchTimeout: ReturnType<typeof setTimeout> | null = null
  const onSearch = () => {
    if (searchTimeout) clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
      serverOptions.value.page = 1
      fetchData()
    }, 300)
  }

  if (options.watchCompanyId !== false) {
    watch(getCompanyId, (newId) => {
      if (!newId) return
      serverOptions.value.page = 1
      fetchData()
    })
  }

  return {
    dataTable,
    search,
    serverOptions,
    serverItemsLength,
    items,
    loading,
    headers,
    fetchData,
    onSearch,
    updateServerOptions,
  }
}
